import React from 'react';
import { Container, Typography, Box, Paper, Divider, List, ListItem, ListItemText } from '@mui/material';
import SEO from '../components/SEO';

const DeleteAccountPage = () => {
  return (
    <>
      <SEO
        title="Delete Your Account - Couplendar"
        description="How to request deletion of your Couplendar account and data."
        url="https://couplendar.com/delete-account"
      />
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Paper elevation={3} sx={{ p: 4 }}>
          <Typography variant="h3" component="h1" gutterBottom>
            Delete Your Couplendar Account
          </Typography>

          <Divider sx={{ my: 3 }} />

          <Box sx={{ '& > *': { mb: 3 } }}>
            <section>
              <Typography variant="h5" component="h2" gutterBottom>
                How to request deletion
              </Typography>
              <Typography variant="body1" paragraph>
                You can delete your Couplendar account and its associated data directly from the app or website:
              </Typography>
              <List sx={{ listStyleType: 'decimal', pl: 4, '& .MuiListItem-root': { display: 'list-item', pl: 0 } }}>
                <ListItem>
                  <ListItemText primary="Sign in to Couplendar (mobile app or couplendar.com)." />
                </ListItem>
                <ListItem>
                  <ListItemText primary="Go to Profile." />
                </ListItem>
                <ListItem>
                  <ListItemText primary='Scroll to the "Danger Zone" section and select Delete Account.' />
                </ListItem>
                <ListItem>
                  <ListItemText primary="Confirm the deletion when prompted." />
                </ListItem>
              </List>
              <Typography variant="body1" paragraph>
                If you're unable to sign in but still want your account and data deleted, email us at{' '}
                <a href="mailto:support@couplendar.com">support@couplendar.com</a> from the email address associated with your account, and we'll process the deletion manually.
              </Typography>
            </section>

            <section>
              <Typography variant="h5" component="h2" gutterBottom>
                Deleting specific data without deleting your account
              </Typography>
              <Typography variant="body1" paragraph>
                You don't have to delete your whole account to remove specific data. While signed in, you can:
              </Typography>
              <List sx={{ listStyleType: 'disc', pl: 4, '& .MuiListItem-root': { display: 'list-item', pl: 0 } }}>
                <ListItem>
                  <ListItemText primary="Delete an individual day's chore or time-off entry from Today or Calendar (swipe or use the delete/reset action on the entry)." />
                </ListItem>
                <ListItem>
                  <ListItemText primary='Delete an entire chore or time-off type you created, from Chores & Timeoff management (trash icon next to the item).' />
                </ListItem>
                <ListItem>
                  <ListItemText primary="Remove a comment you left on a chore entry." />
                </ListItem>
                <ListItem>
                  <ListItemText primary="Remove your profile or relationship picture from Profile / Relationship settings." />
                </ListItem>
              </List>
              <Typography variant="body1" paragraph>
                These deletions take effect immediately and don't require contacting support. If you'd rather have us delete specific data on your behalf, email{' '}
                <a href="mailto:support@couplendar.com">support@couplendar.com</a> describing what to remove.
              </Typography>
            </section>

            <section>
              <Typography variant="h5" component="h2" gutterBottom>
                What gets deleted
              </Typography>
              <Typography variant="body1" paragraph>
                Requesting account deletion permanently removes:
              </Typography>
              <List sx={{ listStyleType: 'disc', pl: 4, '& .MuiListItem-root': { display: 'list-item', pl: 0 } }}>
                <ListItem><ListItemText primary="Your profile information (name, email, profile picture)" /></ListItem>
                <ListItem><ListItemText primary="Your relationship data, chores, time-off entries, and comments" /></ListItem>
                <ListItem><ListItemText primary="Your notifications and push notification tokens" /></ListItem>
              </List>
              <Typography variant="body1" paragraph>
                Your partner's own account and data are not affected — only your side of a shared relationship record is removed.
              </Typography>
            </section>

            <section>
              <Typography variant="h5" component="h2" gutterBottom>
                Retention period
              </Typography>
              <Typography variant="body1" paragraph>
                Personal data is permanently removed from our production systems within 30 days of your deletion request. Data present in encrypted backups is removed according to our backup retention schedule, within a maximum of 90 days.
              </Typography>
            </section>

            <section>
              <Typography variant="h5" component="h2" gutterBottom>
                Questions?
              </Typography>
              <Typography variant="body1" paragraph>
                See our <a href="/privacy-policy">Privacy Policy</a> for full details on how we handle your data, or contact <a href="mailto:support@couplendar.com">support@couplendar.com</a>.
              </Typography>
            </section>
          </Box>
        </Paper>
      </Container>
    </>
  );
};

export default DeleteAccountPage;
